import cx from 'classnames'
import { Text } from 'blaze-ui/Text/Text'
import { Checkbox } from 'blaze-ui/Checkbox/Checkbox'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { IMediaFile } from 'hooks/queries/brandKits/useInfiniteBrandKitMediaFiles'
import Styles from './MediaSection.module.scss'
import { Pill } from 'components/Pill/Pill'
import dayjs from 'dayjs'
import { AlmanacFile } from 'types'
import { VideoDuration } from 'components/VideoDuration/VideoDuration'

interface MediaSectionProps {
  isLoading: boolean
  mediaFiles: IMediaFile[]
  selectedMediaFileIds: Set<number>
  existingMediaFileIds: Set<number>
  onToggle: (mediaFileId: number) => void
}

export const MediaSection = ({
  isLoading,
  mediaFiles,
  selectedMediaFileIds,
  existingMediaFileIds,
  onToggle,
}: MediaSectionProps) => {
  if (isLoading) {
    return (
      <div className={Styles.mediaGrid}>
        {Array.from({ length: 8 }).map((_, i) => (
          <LoadingShimmerer key={i} className={Styles.mediaThumbnailShimmer} borderRadius={8} />
        ))}
      </div>
    )
  }

  if (mediaFiles.length === 0) {
    return (
      <div className={Styles.empty}>
        <Text variant="secondary" color="var(--dark-40)">
          No media files in your Brand Kit
        </Text>
      </div>
    )
  }

  return (
    <div className={Styles.mediaGrid}>
      {mediaFiles.map((file) => {
        const alreadyAdded = existingMediaFileIds.has(file.media_file_id)
        const isSelected = selectedMediaFileIds.has(file.media_file_id)
        const isImage = file.mime_type.includes('image')

        return (
          <div className={Styles.mediaThumbnailContainer}>
            <button
              key={file.media_file_id}
              type="button"
              className={cx(Styles.mediaThumbnail, {
                [Styles.mediaSelected]: isSelected,
                [Styles.disabled]: alreadyAdded,
              })}
              onClick={() => !alreadyAdded && onToggle(file.media_file_id)}
              aria-pressed={isSelected}
              aria-label={file.title || 'Media file'}
            >
              {isImage ? (
                <img
                  src={file.thumbnail_url || file.source_url}
                  alt={file.title || ''}
                  className={Styles.thumbnailImg}
                />
              ) : (
                <video src={file.source_url} className={Styles.thumbnailImg} muted />
              )}
              {(isSelected || alreadyAdded) && <div className={Styles.thumbnailOverlay} />}
              <div className={Styles.thumbnailCheck}>
                <Checkbox size={20} isSelected={isSelected || alreadyAdded} />
              </div>
              {!!file.most_recent_media_usage && (
                <Pill size="sm" className={Styles.usedBadge}>
                  Used
                </Pill>
              )}
              {!isImage && file.duration && <VideoDuration duration={file.duration} />}
            </button>

            <div className={Styles.imageInfo}>
              <div className={Styles.name}>
                <Text variant="secondary" color="var(--dark-90)" lineClamp={1}>
                  {file.title}
                </Text>
              </div>
              <div className={Styles.typeAndDate}>
                <Text variant="metadata" color="var(--dark-40)">
                  {file.mime_type.includes('image') ? 'Image' : ''}
                  {file.mime_type.includes('video') ? 'Video' : ''}
                </Text>
                <Text variant="metadata" color="var(--dark-40)">
                  •
                </Text>
                <Text variant="metadata" color="var(--dark-40)">
                  {dayjs((file as AlmanacFile).created_at).isSame(dayjs(), 'day')
                    ? 'Uploaded Today'
                    : `Uploaded ${dayjs((file as AlmanacFile).created_at).fromNow()}`}
                </Text>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
